import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Calculator,
  DollarSign,
  Clock,
  Percent,
  ArrowRight,
  Check,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  type SelectOption,
} from "@/components/ui";
import { formatPrice, calculateMonthlyPayment } from "@/utils/format";
import { useSeo } from "@/lib/seo";

const termOptions: SelectOption[] = [
  { value: "36", label: "36 months" },
  { value: "48", label: "48 months" },
  { value: "60", label: "60 months" },
  { value: "72", label: "72 months" },
  { value: "84", label: "84 months" },
];

const benefits = [
  "Competitive rates starting at 4.9% APR",
  "No hidden fees or prepayment penalties",
  "Quick pre-approval in minutes",
  "Flexible terms from 36-84 months",
  "Multiple lender options",
  "Protect your purchase with GAP insurance",
];

export function FinancingPage() {
  useSeo({
    title: "Financing | BESPOKIAN | Auto",
    description:
      "Estimate monthly payments and get pre-approved in minutes. Competitive rates and flexible terms.",
    image: "/home-images/quick-actions/payment-calculator.jpg",
  });

  const [vehiclePrice, setVehiclePrice] = useState(32500);
  const [downPayment, setDownPayment] = useState(5000);
  const [termMonths, setTermMonths] = useState(60);
  const [apr, setApr] = useState(6.9);
  const [tradeInValue, setTradeInValue] = useState(0);

  const loanAmount = vehiclePrice - downPayment - tradeInValue;

  const monthlyPayment = useMemo(() => {
    return calculateMonthlyPayment(
      vehiclePrice * 100,
      (downPayment + tradeInValue) * 100,
      apr,
      termMonths,
    );
  }, [vehiclePrice, downPayment, tradeInValue, apr, termMonths]);

  const totalInterest = useMemo(() => {
    return monthlyPayment * termMonths - loanAmount * 100;
  }, [monthlyPayment, termMonths, loanAmount]);

  const totalCost = useMemo(() => {
    return monthlyPayment * termMonths + (downPayment + tradeInValue) * 100;
  }, [monthlyPayment, termMonths, downPayment, tradeInValue]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero (match BrandPage / ShopSlugPage) */}
      <header className="relative min-h-[70vh] flex items-start justify-center pt-28 md:pt-32 pb-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-gray-900"
          style={{
            backgroundImage: `url('/home-images/heroes/payment-calculator.webp')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/60" />
        <div className="absolute inset-0 bg-black/35" />

        <div className="relative z-10 w-full">
          <div className="text-center px-6 max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="font-display text-5xl md:text-6xl text-white mb-4 leading-tight tracking-tight">
                Financing Made Simple
              </h1>
              <p className="text-white text-lg md:text-xl max-w-2xl mx-auto">
                Get pre-approved in minutes. Competitive rates, flexible terms,
                and no hidden fees.
              </p>
              <div className="w-24 h-px bg-white mx-auto mt-6" />
            </div>
          </div>
        </div>
      </header>

      <div className="bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Calculator */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-primary-100 rounded-lg p-2">
                      <Calculator className="h-6 w-6 text-primary-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Payment Calculator
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Vehicle Price ($)"
                      type="number"
                      value={vehiclePrice}
                      onChange={(e) => setVehiclePrice(Number(e.target.value))}
                      min={0}
                    />
                    <Input
                      label="Down Payment ($)"
                      type="number"
                      value={downPayment}
                      onChange={(e) => setDownPayment(Number(e.target.value))}
                      min={0}
                      max={vehiclePrice}
                    />
                    <Input
                      label="Trade-In Value ($)"
                      type="number"
                      value={tradeInValue}
                      onChange={(e) => setTradeInValue(Number(e.target.value))}
                      min={0}
                      helperText="Have a trade-in? Enter its value here."
                    />
                    <Select
                      label="Loan Term"
                      options={termOptions}
                      value={termMonths.toString()}
                      onChange={(e) => setTermMonths(Number(e.target.value))}
                    />
                    <Input
                      label="APR (%)"
                      type="number"
                      value={apr}
                      onChange={(e) => setApr(Number(e.target.value))}
                      min={0}
                      max={30}
                      step={0.1}
                      helperText="Rate depends on credit score and term."
                    />
                  </div>

                  <hr className="my-8" />

                  {/* Results */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="text-center">
                      <div className="bg-gray-100 rounded-lg p-3 mb-2 inline-block">
                        <DollarSign className="h-6 w-6 text-gray-600" />
                      </div>
                      <p className="text-sm text-gray-500">Monthly Payment</p>
                      <p className="text-2xl font-bold text-primary-600">
                        {formatPrice(monthlyPayment)}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="bg-gray-100 rounded-lg p-3 mb-2 inline-block">
                        <Calculator className="h-6 w-6 text-gray-600" />
                      </div>
                      <p className="text-sm text-gray-500">Loan Amount</p>
                      <p className="text-xl font-semibold text-gray-900">
                        {formatPrice(loanAmount * 100)}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="bg-gray-100 rounded-lg p-3 mb-2 inline-block">
                        <Percent className="h-6 w-6 text-gray-600" />
                      </div>
                      <p className="text-sm text-gray-500">Total Interest</p>
                      <p className="text-xl font-semibold text-gray-900">
                        {formatPrice(totalInterest)}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="bg-gray-100 rounded-lg p-3 mb-2 inline-block">
                        <Clock className="h-6 w-6 text-gray-600" />
                      </div>
                      <p className="text-sm text-gray-500">Total Cost</p>
                      <p className="text-xl font-semibold text-gray-900">
                        {formatPrice(totalCost)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 p-4 bg-primary-50 rounded-lg">
                    <p className="text-sm text-primary-800">
                      <strong>Note:</strong> This is an estimate only. Your
                      actual payment may vary based on credit approval, taxes,
                      fees, and final terms.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* CTA Card */}
              <Card>
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Ready to Get Started?
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Get pre-approved with no impact to your credit score.
                  </p>
                  <Button className="w-full" size="lg">
                    Apply Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <p className="mt-4 text-xs text-gray-500">
                    Takes only 2-3 minutes
                  </p>
                </CardContent>
              </Card>

              {/* Benefits */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Why Finance With Us?
                  </h3>
                  <ul className="space-y-3">
                    {benefits.map((benefit) => (
                      <li
                        key={benefit}
                        className="flex items-start gap-2 text-sm"
                      >
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Trade-In CTA */}
              <Card className="bg-gray-900 text-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-2">
                    Have a Trade-In?
                  </h3>
                  <p className="text-gray-300 text-sm mb-4">
                    Get an instant offer on your current vehicle to lower your
                    payments.
                  </p>
                  <Link to="/trade-in">
                    <Button
                      variant="outline"
                      className="w-full border-white text-white hover:bg-white/10"
                    >
                      Get Trade-In Value
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
